import React from 'react';
import { Button, Box, Stack, Typography, Divider, TextField } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { submitDietAction } from '../../../../../services/request';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';


export default function PLDietForm (props) {
  const { data, submitCallback, handleClose, allSeasons, allEvents } = props;

  const { loading, mutate } = useMutation(submitDietAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [eventDietPrice, setEventDietPrice] = React.useState(data ? data.diet_price_per_events || [] : []);

  const handleEventDietPriceChange = (index, value) => {
    setEventDietPrice(prev => prev.map((item, i) => i === index ? { ...item, price: value } : item));
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('To pole nie może być puste'),
    events: Yup.array().min(1, 'Przynajmniej jedna impreza musi zostać wybrana')
      .required()
  });

  const initialValues = {
    name: data ? data.name || '' : '',
    season: data ? data.season || allSeasons[allSeasons.length - 1].value : allSeasons[allSeasons.length - 1].value,
    events: data ? data.events || [] : []
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values, data ? 'PUT' : 'POST', data ? data.id : undefined);
    if (!error) {
      submitCallback(payload, data ? 'edit' : 'created', 'success', data ? 'Zaktualizowano diete' : 'Dodano nową diete', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się dodać nowej diety');
      setMessage('');
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(data, 'delete', 'success', `Usunięto diete ${data.name}`, '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się usunąć diety ${data.name}`);
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      values.diet_price_per_events = eventDietPrice;
      handleSubmit(values);
    }
  });

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Box display="flex" flexDirection="column" mt={1} p={1}>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Nazwa</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="name"
              id="name"
              type="text"
              placeholder="nazwa diety"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Sezon</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              id="season"
              name="season"
              options={allSeasons}
              value={formik.values.season}
              onChange={e => {
                formik.handleChange(e);
                formik.setFieldValue('events', []);
                setEventDietPrice([]);
              }}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Imprezy</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              multiple
              id="events"
              name="events"
              options={allEvents[formik.values.season]}
              value={formik.values.events}
              onChange={e => {
                formik.handleChange(e);
                setEventDietPrice(allEvents[formik.values.season].filter(event => e.target.value.includes(event.value)).map(event => ({ id: event.value, name: event.label, price: 0 })));
              }}
              error={formik.touched.events && Boolean(formik.errors.events)}
              helperText={formik.touched.events && formik.errors.events}
            />
          </Stack>
          <Divider sx={{ mb: 2 }}>Cena diety dla wybranych turnusów</Divider>
          <Stack justifyContent="center" alignItems="center">
            {eventDietPrice.map((item, index) => (
              <Stack key={item.id} direction="row" alignItems="center" sx={{ width: '60%' }} mb={2}>
                <Typography variant="subtitle2" flex="1 1 30%">{item.name}</Typography>
                <TextField
                  sx={{ flex: '1 1 65%' }}
                  type="number" size="small"
                  value={eventDietPrice[index].price}
                  onChange={e => handleEventDietPriceChange(index, e.target.value)}
                />
                <Typography variant="subtitle2" flex="1 1 5%">zł</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
          disabled={loading}
        >
          <AddIcon />
          Zapisz
        </Button>
        { data
          ? <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
            disabled={loading}
          >
            <DeleteIcon />
          Usuń
          </Button>
          : null}
      </form>
      <ConfirmDialog
        title="Usunąć diete?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
      >
        Czy na pewno chcesz usunąć tą diete?
      </ConfirmDialog>
    </>

  );
};
